import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

// Auth
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    getMe: () => api.get('/auth/me'),
};

// Books
export const booksAPI = {
    getAll: () => api.get('/books'),
    getOne: (id) => api.get(`/books/${id}`),
    create: (data) => api.post('/books', data),
    getChapters: (bookId) => api.get(`/books/${bookId}/chapters`),
    getChaptersPreview: (bookId) => api.get(`/books/${bookId}/chapters/preview`),
    createChapter: (bookId, data) => api.post(`/books/${bookId}/chapters`, data),
};

// Chapters
export const chaptersAPI = {
    getOne: (id) => api.get(`/chapters/${id}`),
    update: (id, data) => api.put(`/chapters/${id}`, data),
    delete: (id) => api.delete(`/chapters/${id}`),
};

// Chat
export const chatAPI = {
    getSessions: () => api.get('/chat/sessions'),
    getSession: (id) => api.get(`/chat/sessions/${id}`),
    createSession: (data) => api.post('/chat/sessions', data),
    toggleSession: (id) => api.put(`/chat/sessions/${id}/toggle`),
    getMessages: (sessionId) => api.get(`/chat/sessions/${sessionId}/messages`),
    sendMessage: (sessionId, data) => api.post(`/chat/sessions/${sessionId}/messages`, data),
};

// Payments
export const paymentsAPI = {
    createCheckout: (originUrl, productType) => api.post('/payments/create-checkout', { 
        origin_url: originUrl,
        product_type: productType 
    }),
    getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
    createRescheduleCheckout: (originUrl, bookingId, newDate, newTime) => api.post('/payments/reschedule-checkout', {
        origin_url: originUrl,
        booking_id: bookingId,
        new_date: newDate,
        new_time: newTime
    }),
    createResumeCheckout: (data) => api.post('/payments/resume-checkout', data),
};

// Products
export const productsAPI = {
    getBookInfo: () => api.get('/products/book'),
    getCourseInfo: () => api.get('/products/course'),
    getMemberships: () => api.get('/products/memberships'),
};

// Airlines
export const airlinesAPI = {
    getHiringAirlines: () => api.get('/airlines/hiring'),
    getPreview: () => api.get('/airlines/hiring/preview'),
};

// Coaching
export const coachingAPI = {
    getProducts: () => api.get('/coaching/products'),
    getAvailability: (date) => api.get(`/coaching/availability?date=${date}`),
    getPolicy: () => api.get('/coaching/policy'),
    createBooking: (data) => api.post('/coaching/book', data),
    getBookings: () => api.get('/coaching/bookings'),
    getBooking: (id) => api.get(`/coaching/bookings/${id}`),
    getBookingDetails: (id) => api.get(`/coaching/bookings/${id}/session`),
    payForBooking: (id) => api.post(`/coaching/bookings/${id}/pay`),
    rescheduleBooking: (id, data) => api.post(`/coaching/bookings/${id}/reschedule`, data),
    // VIP Free Session
    getVipSessionStatus: () => api.get('/coaching/vip-session-status'),
    bookVipFreeSession: (data) => api.post('/coaching/book-vip-free', data),
};

// Reading Progress
export const progressAPI = {
    update: (data) => api.post('/reading-progress', data),
    get: (bookId) => api.get(`/reading-progress/${bookId}`),
};

// Services (Coaching & Resume)
export const servicesAPI = {
    getConfig: () => api.get('/services/config'),
};

export default api;
